import { useEffect, useState } from 'react';
import { api } from './api';
import { plural } from './formato';
import { useSessao } from './sessao';
import { Area, Aviso, Dialogo, ErroDaApi, LinkProtegido, useEstreito } from './ui';

/**
 * O evento da janela que abre o «Faltam … para o laudo» no celular (310).
 *
 * A faixa da ficha (o «Faltam N», em `Ficha.tsx`) e esta lista são irmãs em
 * cada ficha, sem estado em comum; um evento é o fio mais curto entre as
 * duas — o mesmo jeito do «Relatar» da 308.
 */
export const ABRIR_PENDENCIAS = 'labmanager:abrir-pendencias';

export interface PendenciaDetalhada {
  codigo: string;
  texto: string;
  /* `false` = desvio de norma que o laudo já imprime; aparece na lista
     e NÃO tranca o ensaio (0090). */
  trava: boolean;
  justificada: boolean;
  justificativa: string | null;
}

// ── pendências ───────────────────────────────────────────────────────

/**
 * O que falta, sempre à vista.
 *
 * A lista vem do servidor. Repeti-la aqui em JavaScript criaria duas
 * verdades sobre o mesmo ensaio, e a que aparece na tela é a que o técnico
 * acredita.
 */
export function Pendencias({ itens, id, detalhe, aoJustificar }: {
  itens: string[];
  id: string;
  detalhe?: PendenciaDetalhada[];
  aoJustificar?: () => void;
}) {
  const { pode } = useSessao();
  const [alvo, setAlvo] = useState<PendenciaDetalhada | null>(null);
  /*
   * 310 (spec 06 §7.3): no celular a caixa grande do que falta empurrava o
   * primeiro campo da etapa para fora da tela. Vira uma linha que se abre —
   * já aberta quando se chega por «Faltam N ›» (o endereço com
   * `#pendencias`, ou o toque na faixa, que avisa pelo evento).
   */
  const estreito = useEstreito(640);
  const [aberta, setAberta] = useState(
    () => typeof window !== 'undefined' && window.location.hash === '#pendencias');
  useEffect(() => {
    const abrir = () => setAberta(true);
    window.addEventListener(ABRIR_PENDENCIAS, abrir);
    return () => window.removeEventListener(ABRIR_PENDENCIAS, abrir);
  }, []);
  const justificadas = (detalhe ?? []).filter((p) => p.justificada);
  const abertas = (detalhe ?? []).filter((p) => !p.justificada);
  /*
   * `trava` separa o que IMPEDE do que apenas AVISA (0090). Misturar os
   * dois num aviso só ensina a ignorar o aviso: quem lê «faltam 3» e
   * descobre que duas eram recomendação de norma passa a descontar as
   * três da próxima vez.
   */
  const travam = abertas.filter((p) => p.trava);
  const avisam = abertas.filter((p) => !p.trava);

  if (!itens.length && !justificadas.length && !avisam.length) {
    return (
      <Aviso tipo="ok" titulo="O ensaio está completo">
        Todos os dados que o laudo imprime estão lançados.{' '}
        <LinkProtegido caminho={`/ensaios/${id}/laudo`}
          nomeQueda={`laudo-${id.slice(0, 8)}.html`}>
          ver o laudo
        </LinkProtegido>
      </Aviso>
    );
  }

  /*
   * Desde a 0089 a lista TRAVA: sem resolver ou justificar, o ensaio não
   * vai a verificação nem a aprovação. Dizer isso aqui é o que separa
   * «aviso» de «recusa surpresa três telas adiante» — a lição da 0037.
   */
  const faltam = detalhe ? travam.length : itens.length;
  const oQueFalta = faltam > 0 && (
    <Aviso tipo="atencao"
      titulo={`Faltam ${plural(faltam, 'item', 'itens')} para o laudo`}>
      <p className="sub">
        Enquanto estiverem em aberto, este ensaio não vai para
        verificação nem para aprovação. Cada um se resolve — ou recebe
        uma justificativa escrita, que sai impressa no laudo.
      </p>
      <ul className="pendencias">
        {detalhe
          ? travam.map((p) => (
            <li key={p.codigo}>
              {p.texto}
              {pode('ensaio.justificar_pendencia') && (
                <button type="button" className="ligacao"
                  onClick={() => setAlvo(p)}>justificar</button>
              )}
            </li>
          ))
          : itens.map((p) => <li key={p}>{p}</li>)}
      </ul>
    </Aviso>
  );

  return (
    <>
      {oQueFalta && (estreito
        ? (
          /* `onToggle` mantém o estado igual ao que se vê: fechar pelo
             resumo e tocar de novo em «Faltam N» tem que reabrir. */
          <details className="pend-celular" open={aberta}
            onToggle={(e) => setAberta(e.currentTarget.open)}>
            <summary>Faltam {plural(faltam, 'item', 'itens')} para o laudo — ver</summary>
            {oQueFalta}
          </details>
        )
        : oQueFalta)}

      {avisam.length > 0 && (
        <Aviso tipo="info"
          titulo={`${avisam.length} desvio(s) de norma — o laudo diz`}>
          <p className="sub">
            Não impedem a emissão: o ensaio existe e o documento informa o
            que foi feito. Quem assina decide se emite assim.
          </p>
          <ul className="pendencias">
            {avisam.map((p) => (
              <li key={p.codigo}>
                {p.texto}
                {pode('ensaio.justificar_pendencia') && (
                  <button type="button" className="ligacao"
                    onClick={() => setAlvo(p)}>registrar o motivo</button>
                )}
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      {justificadas.length > 0 && (
        <Aviso tipo="info"
          titulo={`${justificadas.length} pendência(s) justificada(s)`}>
          <ul className="pendencias">
            {justificadas.map((p) => (
              <li key={p.codigo}>
                {p.texto}
                <span className="sub">{p.justificativa}</span>
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      {alvo && (
        <JustificarPendencia ensaioId={id} alvo={alvo}
          aoFechar={() => setAlvo(null)}
          aoGravar={() => { setAlvo(null); aoJustificar?.(); }} />
      )}
    </>
  );
}

/**
 * Por que o laudo pode sair com isto em aberto.
 *
 * O campo é longo e o mínimo é de quinze caracteres, de propósito: «ok»
 * num campo obrigatório é o formulário vencendo a regra. A frase escrita
 * aqui acompanha o documento — quem a lê é o cliente, não o sistema.
 */
function JustificarPendencia({ ensaioId, alvo, aoFechar, aoGravar }: {
  ensaioId: string;
  alvo: PendenciaDetalhada;
  aoFechar: () => void;
  aoGravar: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [gravando, setGravando] = useState(false);

  const gravar = async () => {
    setErro(null);
    setGravando(true);
    try {
      await api(
        `/ensaios/${ensaioId}/pendencias/${alvo.codigo}/justificar`,
        { metodo: 'POST', corpo: { motivo } });
      aoGravar();
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  return (
    <Dialogo aberto titulo="Justificar pendência" aoFechar={aoFechar}>
      {erro ? <ErroDaApi erro={erro} /> : null}
      <p><strong>{alvo.texto}</strong></p>
      <Area
        rotulo="Por que o laudo pode sair assim"
        dica="Sai impressa no laudo, com o seu nome e a hora."
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
      />
      <div className="rodape-dialogo">
        <button type="button" className="secundario"
          onClick={aoFechar}>Cancelar</button>
        <button type="button"
          disabled={motivo.trim().length < 15 || gravando}
          onClick={() => { void gravar(); }}>
          {gravando ? 'Gravando…' : 'Justificar'}
        </button>
      </div>
    </Dialogo>
  );
}
